// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for updating asset accounting and depreciation information.
 * Provides functionality to modify financial tracking properties including
 * cost basis, useful life, salvage value, and depreciation model.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { detailedAssetSchema } from '@/asset/api/types';
import { OrgDefaultCurrencyCode } from '@/constants/currency';
import { DepreciationModel } from '@/constants/depreciation-model';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating accounting properties.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const updateAssetAccountingPropertiesResponseSchema = z
  .object({
    data: detailedAssetSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Parameters for updating an asset's accounting properties.
 * All fields are optional to allow partial updates of financial information.
 * Monetary values are stored as strings to maintain precision.
 *
 * @typedef {Object} UpdateAssetAccountingPropertiesParameters
 * @property {string | null} [costBasis] - Initial cost of the asset in dollars
 * @property {number | null} [usefulLife] - Expected lifespan in months
 * @property {string | null} [salvageValue] - Expected end-of-life value in dollars
 * @property {DateTime | null} [inServiceDate] - Date when the asset was put into service
 * @property {DepreciationModel | null} [depreciationModel] - Method used to calculate depreciation
 */
export interface UpdateAssetAccountingPropertiesParameters {
  usefulLife?: number | null;
  // Typed to the app-allowed currencies rather than the full recognized registry,
  // which is what the backend accepts.
  costCurrencyCode?: OrgDefaultCurrencyCode;
  salvageValue?: Currency | null;
  inServiceDate?: DateTime | null;
  depreciationModel?: DepreciationModel | null;
  simpleCostBasis?: Currency | null;
  billOfMaterials?: Currency | null;
  directLabor?: Currency | null;
  allocatedIndirect?: Currency | null;
  freightInbound?: Currency | null;
  freightOutbound?: Currency | null;
  installation?: Currency | null;
  tax?: Currency | null;
  tariffs?: Currency | null;
  interest?: Currency | null;
}

/**
 * Updates the accounting and depreciation properties of an asset.
 * This function handles the API call to modify financial tracking information
 * and validates the response data using Zod schemas. It supports partial
 * updates, allowing modification of specific fields while leaving others unchanged.
 *
 * @param key - Unique identifier of the asset to update
 * @param parameters - Object containing the accounting properties to update
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateAssetAccountingProperties(
  key: string,
  parameters: UpdateAssetAccountingPropertiesParameters
) {
  const url = `/v1/asset/${key}/accounting`;
  const response = await api.patch(url, {
    ...parameters,
    inServiceDate: serializeDateForAPI(parameters.inServiceDate),
    salvageValue: serializeOptionalCurrencyForAPI(parameters.salvageValue),
    simpleCostBasis: serializeOptionalCurrencyForAPI(
      parameters.simpleCostBasis
    ),
    billOfMaterials: serializeOptionalCurrencyForAPI(
      parameters.billOfMaterials
    ),
    directLabor: serializeOptionalCurrencyForAPI(parameters.directLabor),
    allocatedIndirect: serializeOptionalCurrencyForAPI(
      parameters.allocatedIndirect
    ),
    freightInbound: serializeOptionalCurrencyForAPI(parameters.freightInbound),
    freightOutbound: serializeOptionalCurrencyForAPI(
      parameters.freightOutbound
    ),
    installation: serializeOptionalCurrencyForAPI(parameters.installation),
    tax: serializeOptionalCurrencyForAPI(parameters.tax),
    tariffs: serializeOptionalCurrencyForAPI(parameters.tariffs),
    interest: serializeOptionalCurrencyForAPI(parameters.interest),
  });

  return parseResponse(
    updateAssetAccountingPropertiesResponseSchema,
    response.data,
    {
      schemaName: 'UpdateAssetAccountingProperties',
      endpoint: url,
    }
  );
}
